import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { emptyChart } from 'PDF/components/Charts/types';
import { IFinBlockTableRowItem } from 'PDF/components/FinBlockTable';
import { IGoalsTableData } from 'PDF/components/GoalsTable/types';
import { ITextItem } from 'PDF/components/PdfContentText';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { hasDataToRender } from 'PDF/helpers/hasDataToRender';
import {
  checkIsEmptyDataByKey,
  getStages,
  IInfoTableRow,
  isMagazineItemsNotEmpty,
  prepareSectionId,
} from 'PDF/helpers/pdfMapping';
import { getProductDescriptionData } from 'PDF/helpers/productDescriptionMapping';
import { AboutTheCompanyPdf, IAboutCompanyPdfProps } from 'PDF/pages/AboutTheCompany/AboutTheCompanyPdf';
import { getAboutCompanyProps } from 'PDF/pages/AboutTheCompany/mapping';
import { BusinessOperationsPdf } from 'PDF/pages/BusinessOperations';
import { IBusinessOperationsPdfProps } from 'PDF/pages/BusinessOperations/BusinessOperationsPdf';
import {
  getBuildingsPremisesDetails,
  getEquipments,
  getMagazineItems as getMagazineItemsBusinessOperations,
  getProcessStages,
  getSupplierOverviewData,
} from 'PDF/pages/BusinessOperations/mapping';
import { CoverPagePdf } from 'PDF/pages/CoverPage';
import { FinancialPlanPdf } from 'PDF/pages/FinancialPlan';
import { IFinancialPlanPdfProps } from 'PDF/pages/FinancialPlan/FinancialPlanPdf';
import {
  getFundingDirectionsTableData,
  getInvestmentPerformanceRatios,
  getMagazineItems as getMagazineItemsFinancialPlan,
  getOtherPerformanceRatios,
  getPersonnelExpensesData,
  getPlannedExpenses,
  getProfitAndLossProjectionTableData,
  getProjectedBalanceSheet,
  getProjectedCashFlowTableData,
  getVariableExpenses,
} from 'PDF/pages/FinancialPlan/mapping';
import { ImplementationPlanPdf } from 'PDF/pages/ImplementationPlan';
import { IImplementationPlanPdfProps } from 'PDF/pages/ImplementationPlan/ImplementationPlanPdf';
import { LegalFrameworkAndRisksPdf } from 'PDF/pages/LegalFrameworkAndRisks';
import { ILegalFrameworkAndRisksPdfProps } from 'PDF/pages/LegalFrameworkAndRisks/LegalFrameworkAndRisksPdf';
import {
  getListOfActions,
  getListOfRisks,
  getMagazineItems as getMagazineItemsLegalFrameworkAndRisks,
} from 'PDF/pages/LegalFrameworkAndRisks/mapping';
import { ManagementAndOrganizationPdf } from 'PDF/pages/ManagementAndOrganization';
import { IManagementAndOrganizationPdfProps } from 'PDF/pages/ManagementAndOrganization/ManagementAndOrganizationPdf';
import {
  getKeyPersonnelProfiles,
  getMagazineItems as getMagazineItemsManagementAndOrganization,
  getProfiles,
} from 'PDF/pages/ManagementAndOrganization/mapping';
import { MarketAnalysisPdf } from 'PDF/pages/MarketAnalysis';
import {
  getB2B,
  getB2C,
  getCompetitorsComparison,
  getMagazineItems as getMagazineItemsMarketAnalysis,
  getMarketTrends,
  getPriceOverview,
  getProductComparison,
  getStrengthAndWeakness,
} from 'PDF/pages/MarketAnalysis/mapping';
import { IMarketAnalysisPdfProps } from 'PDF/pages/MarketAnalysis/MarketAnalysisPdf';
import { getMarketingAndSalesProps, getSalesPlanTableData } from 'PDF/pages/MarketingAndSales/mapping';
import { IMarketingAndSalesPdfProps, MarketingAndSalesPdf } from 'PDF/pages/MarketingAndSales/MarketingAndSalesPdf';
import { getProductAndServicesProps } from 'PDF/pages/ProductsAndServices/mappings';
import {
  IProductsAndServicesPdfProps,
  ProductsAndServicesPdf,
} from 'PDF/pages/ProductsAndServices/ProductsAndServicesPdf';
import { RaisingAndAllocatingFundsPdf } from 'PDF/pages/RaisingAndAllocatingFunds';
import {
  getFundingStrategy,
  getMagazineItems as getMagazineItemsRaisingAndAllocatingFunds,
  getProjectCostAndFundingNeeds,
} from 'PDF/pages/RaisingAndAllocatingFunds/mapping';
import { IRaisingAndAllocatingFundsPdfProps } from 'PDF/pages/RaisingAndAllocatingFunds/RaisingAndAllocatingFundsPdf';
import {
  getFinancialInformation,
  getFinancingNeeds,
  getMagazineItems as getMagazineItemsSummary,
} from 'PDF/pages/Summary/mapping';
import { ISummaryPdfProps, SummaryPdf } from 'PDF/pages/Summary/SummaryPdf';
import { PrivateSectionsEnum } from 'PDF/pages/TableOfContents/helpers';
import { TableOfContentsPdf } from 'PDF/pages/TableOfContents/TableOfContentsPdf';
import {
  AboutCompanyKeys,
  BusinessOperationsKeys,
  FinancialPlanKeys,
  ImplementationPlanKeys,
  LegalFrameworkAndRisksKeys,
  ManagementAndOrganizationKeys,
  MarketAnalysisKeys,
  MarketingAndSalesKeys,
  ProductsAndServicesKeys,
  RaisingAndAllocatingFundsKeys,
  SectionsAvailability,
  SectionsAvailabilityKeys,
  SectionsType,
  SummaryKeys,
} from 'PDF/types/pdfSections';
import { IIndustruPictureItem, SectionFileName } from 'services/businessPlan/types';

import { IOpeningBalanceSheets } from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/utils/form';
import { adjustmentsForLinkedFields } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/AdjustmentsForForm/utils/form';
import { operatingAssetsAndLiabilitiesLinkedFields } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/OperatingAssetsAndLiabilitiesForm/utils/form';
import { operatingActivitiesRows } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/utils/form';
import { IBusinessToolForm } from 'components/BusinessToolForm/utils/form';

import { getIsGoalsAndObjectivesTableEmpty } from 'helpers/tableHelpers';

export const getSectionKey = (key: string) => {
  switch (key) {
    case 'FOR_BANK_EXECUTIVE_SUMMARY': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.SECTION_SUMMARY);
    }
    case 'FOR_INVESTORS_EXECUTIVE_SUMMARY': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.SECTION_SUMMARY);
    }
    case 'FOR_BANK_ABOUT_COMPANY': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.ABOUT_COMPANY);
    }
    case 'FOR_INVESTORS_COMPANY_AND_PROJECT': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.ABOUT_COMPANY);
    }
    case 'FOR_BANK_PRODUCTS_AND_SERVICES': {
      return 'productAndServices';
    }
    case 'FOR_BANK_MARKETING_AND_SALES':
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.MARKETING_AND_SALES);
    case 'FOR_INVESTORS_MARKETING_AND_SALES':
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.MARKETING_AND_SALES);
    case 'FOR_BANK_MARKET_ANALYSIS': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.MARKET_ANALYSIS);
    }
    case 'FOR_INVESTORS_MARKET_ANALYSIS': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.MARKET_ANALYSIS);
    }
    case 'FOR_BANK_BUSINESS_OPERATIONS': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.BUSINESS_OPERATIONS);
    }
    case 'FOR_INVESTORS_BUSINESS_OPERATIONS': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.BUSINESS_OPERATIONS);
    }
    case 'FOR_BANK_MANAGEMENT_AND_ORGANIZATION': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.MANAGEMENT_AND_ORGANIZATION);
    }
    case 'FOR_INVESTORS_MANAGEMENT_AND_ORGANIZATION': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.MANAGEMENT_AND_ORGANIZATION);
    }
    case 'FOR_BANK_LEGAL_FRAMEWORKS_AND_RISKS': {
      return 'legalFrameworkAndRisks';
    }
    case 'FOR_BANK_IMPLEMENTATION_PLAN': {
      return 'implementationPlan';
    }
    case 'FOR_BANK_RAISING_AND_ALLOCATING_FUNDS': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.RAISING_AND_ALLOCATING_FUNDS);
    }
    case 'FOR_INVESTORS_RAISING_AND_ALLOCATING_FUNDS': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.RAISING_AND_ALLOCATING_FUNDS);
    }
    case 'FOR_BANK_FINANCIAL_PLAN': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.FINANCIAL_PLAN);
    }
    case 'FOR_INVESTORS_FINANCIAL_PLAN': {
      return prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.FINANCIAL_PLAN);
    }
    default:
      return 'coverPage';
  }
};

export const transformSectionPictures = (industryPictures: IIndustruPictureItem[] | undefined) =>
  industryPictures?.reduce<Partial<Record<SectionFileName, string>>>(
    (result, curItem) => ({
      ...result,
      [curItem.filename.replace('.jpg', '')]: curItem.url,
    }),
    {},
  );

// export const getSectionWrapper = (
//   section: string,
//   handler: (key: string, props: Record<string, any>) => void,
//   industryPictures: IIndustruPictureItem[] | undefined,
// ) => {
//   const sectionPictures = transformSectionPictures(industryPictures);

//   switch (section) {
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.SECTION_SUMMARY):
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.SECTION_SUMMARY):
//       return <Summary saveSectionProps={handler} sectionPicture={sectionPictures?.Section1} />;
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.ABOUT_COMPANY):
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.ABOUT_COMPANY):
//       return <AboutTheCompany saveSectionProps={handler} sectionPicture={sectionPictures?.Section2} />;
//     case 'productAndServices':
//       return <ProductsAndServices saveSectionProps={handler} sectionPicture={sectionPictures?.Section3} />;
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.MARKET_ANALYSIS):
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.MARKET_ANALYSIS):
//       return <MarketAnalysis saveSectionProps={handler} sectionPicture={sectionPictures?.Section4} />;
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.BUSINESS_OPERATIONS):
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.BUSINESS_OPERATIONS):
//       return <BusinessOperations saveSectionProps={handler} sectionPicture={sectionPictures?.Section5} />;
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.MANAGEMENT_AND_ORGANIZATION):
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.MANAGEMENT_AND_ORGANIZATION):
//       return <ManagementAndOrganization saveSectionProps={handler} sectionPicture={sectionPictures?.Section6} />;
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.RAISING_AND_ALLOCATING_FUNDS):
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.RAISING_AND_ALLOCATING_FUNDS):
//       return <RaisingAndAllocatingFunds saveSectionProps={handler} sectionPicture={sectionPictures?.Section7} />;
//     case 'legalFrameworkAndRisks':
//       return <LegalFrameworkAndRisks saveSectionProps={handler} sectionPicture={sectionPictures?.Section8} />;
//     case 'implementationPlan':
//       return <ImplementationPlan saveSectionProps={handler} sectionPicture={sectionPictures?.Section9} />;
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.FINANCIAL_PLAN):
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.FINANCIAL_PLAN):
//       return <FinancialPlan saveSectionProps={handler} sectionPicture={sectionPictures?.Section10} />;
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.MARKETING_AND_SALES):
//     case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.MARKETING_AND_SALES):
//       return <MarketingAndSales saveSectionProps={handler} sectionPicture={sectionPictures?.Section11} />;
//     default:
//       return (
//         <>
//           <CoverPage saveSectionProps={handler} />
//           <TableOfContents saveSectionProps={handler} />
//         </>
//       );
//   }
// };

export const getSectionNumber = (sections: SectionsAvailabilityKeys[], sectionKey: SectionsAvailabilityKeys) => {
  const index = sections.findIndex((section) => section === sectionKey);

  return `${index < 9 ? '0' : ''}${index === -1 ? 0 : index + 1}`;
};

export const getSectionPdf = (
  section: string,
  sections: SectionsType,
  businessPlanType: IBusinessToolType,
  industryPictures: IIndustruPictureItem[] | undefined,
  privateSections: { [key in PrivateSectionsEnum]?: boolean },
  sectionsAvailability: SectionsAvailability,
  isStandardOrLightTariff: boolean,
  isExamplePage: boolean,
  openingBalanceSheetData?: IOpeningBalanceSheets,
) => {
  const sectionPictures = transformSectionPictures(industryPictures);
  const summaryFilledKeys = (Object.keys(sectionsAvailability.summary) as SummaryKeys[]).filter(
    (key) => !!sectionsAvailability.summary[key],
  );
  const aboutCompanyFilledKeys = (Object.keys(sectionsAvailability.aboutCompany) as AboutCompanyKeys[]).filter(
    (key) => !!sectionsAvailability.aboutCompany[key],
  );
  const productsAndServicesFilledKeys = (
    Object.keys(sectionsAvailability.productsAndServices) as ProductsAndServicesKeys[]
  ).filter((key) => !!sectionsAvailability.productsAndServices[key]);
  const marketAnalysisFilledKeys = (Object.keys(sectionsAvailability.marketAnalysis) as MarketAnalysisKeys[]).filter(
    (key) => !!sectionsAvailability.marketAnalysis[key],
  );
  const marketingAndSalesFilledKeys = (
    Object.keys(sectionsAvailability.marketingAndSales) as MarketingAndSalesKeys[]
  ).filter((key) => !!sectionsAvailability.marketingAndSales[key]);
  const businessOperationsFilledKeys = (
    Object.keys(sectionsAvailability.businessOperations) as BusinessOperationsKeys[]
  ).filter((key) => !!sectionsAvailability.businessOperations[key]);
  const managementAndOrganizationFilledKeys = (
    Object.keys(sectionsAvailability.managementAndOrganization) as ManagementAndOrganizationKeys[]
  ).filter((key) => !!sectionsAvailability.managementAndOrganization[key]);
  const implementationPlanFilledKeys = (
    Object.keys(sectionsAvailability.implementationPlan) as ImplementationPlanKeys[]
  ).filter((key) => !!sectionsAvailability.implementationPlan[key]);
  const raisingAndAllocatingFundsFilledKeys = (
    Object.keys(sectionsAvailability.raisingAndAllocatingFunds) as RaisingAndAllocatingFundsKeys[]
  ).filter((key) => !!sectionsAvailability.raisingAndAllocatingFunds[key]);
  const legalFrameworkAndRisksFilledKeys = (
    Object.keys(sectionsAvailability.legalFrameworkAndRisks) as LegalFrameworkAndRisksKeys[]
  ).filter((key) => !!sectionsAvailability.legalFrameworkAndRisks[key]);
  const financialPlanFilledKeys = (Object.keys(sectionsAvailability.financialPlan) as FinancialPlanKeys[]).filter(
    (key) => !!sectionsAvailability.financialPlan[key],
  );
  const sectionsData = {
    summary: !!summaryFilledKeys.length,
    aboutCompany: !!aboutCompanyFilledKeys.length,
    productsAndServices: !!productsAndServicesFilledKeys.length,
    marketAnalysis: !!marketAnalysisFilledKeys.length,
    marketingAndSales: !!marketingAndSalesFilledKeys.length,
    businessOperations: !!businessOperationsFilledKeys.length,
    managementAndOrganization: !!managementAndOrganizationFilledKeys.length,
    implementationPlan: !!implementationPlanFilledKeys.length,
    raisingAndAllocatingFunds: !!raisingAndAllocatingFundsFilledKeys.length,
    legalFrameworkAndRisks: !!legalFrameworkAndRisksFilledKeys.length,
    financialPlan: !!financialPlanFilledKeys.length,
  };
  const filledSections = (Object.keys(sectionsData) as SectionsAvailabilityKeys[]).filter((key) => !!sectionsData[key]);

  switch (section) {
    case 'coverPage':
      return (
        <>
          <CoverPagePdf
            {...sections[section]}
            sectionPicture={sectionPictures?.Title}
            isStandardOrLightTariff={isStandardOrLightTariff}
          />
          <TableOfContentsPdf
            privateSections={privateSections}
            businessPlanType={businessPlanType}
            sectionPicture={sectionPictures?.Contents}
            isStandardOrLightTariff={isStandardOrLightTariff}
            sectionsAvailability={sectionsAvailability}
            isExamplePage={isExamplePage}
          />
        </>
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.SECTION_SUMMARY):
      return (
        <SummaryPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section1}
          availability={sectionsAvailability.summary}
          sectionNumber={getSectionNumber(filledSections, 'summary')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.SECTION_SUMMARY):
      return (
        <SummaryPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section1}
          businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
          availability={sectionsAvailability.summary}
          sectionNumber={getSectionNumber(filledSections, 'summary')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.ABOUT_COMPANY):
      return (
        <AboutTheCompanyPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section2}
          availability={sectionsAvailability.aboutCompany}
          sectionNumber={getSectionNumber(filledSections, 'aboutCompany')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.ABOUT_COMPANY):
      return (
        <AboutTheCompanyPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section2}
          availability={sectionsAvailability.aboutCompany}
          businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
          sectionNumber={getSectionNumber(filledSections, 'aboutCompany')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case PDF_SECTIONS.PRODUCT_AND_SERVICES:
      return (
        <ProductsAndServicesPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section3}
          availability={sectionsAvailability.productsAndServices}
          sectionNumber={getSectionNumber(filledSections, 'productsAndServices')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.MARKET_ANALYSIS):
      return (
        <MarketAnalysisPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section4}
          availability={sectionsAvailability.marketAnalysis}
          sectionNumber={getSectionNumber(filledSections, 'marketAnalysis')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.MARKET_ANALYSIS):
      return (
        <MarketAnalysisPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section4}
          availability={sectionsAvailability.marketAnalysis}
          businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
          sectionNumber={getSectionNumber(filledSections, 'marketAnalysis')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.MARKETING_AND_SALES):
      return (
        <MarketingAndSalesPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section5}
          availability={sectionsAvailability.marketingAndSales}
          sectionNumber={getSectionNumber(filledSections, 'marketingAndSales')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.MARKETING_AND_SALES):
      return (
        <MarketingAndSalesPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section5}
          availability={sectionsAvailability.marketingAndSales}
          businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
          sectionNumber={getSectionNumber(filledSections, 'marketingAndSales')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.BUSINESS_OPERATIONS):
      return (
        <BusinessOperationsPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section6}
          availability={sectionsAvailability.businessOperations}
          sectionNumber={getSectionNumber(filledSections, 'businessOperations')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.BUSINESS_OPERATIONS):
      return (
        <BusinessOperationsPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section6}
          availability={sectionsAvailability.businessOperations}
          businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
          sectionNumber={getSectionNumber(filledSections, 'businessOperations')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.MANAGEMENT_AND_ORGANIZATION):
      return (
        <ManagementAndOrganizationPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section7}
          availability={sectionsAvailability.managementAndOrganization}
          sectionNumber={getSectionNumber(filledSections, 'managementAndOrganization')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.MANAGEMENT_AND_ORGANIZATION):
      return (
        <ManagementAndOrganizationPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section7}
          availability={sectionsAvailability.managementAndOrganization}
          businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
          sectionNumber={getSectionNumber(filledSections, 'managementAndOrganization')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.RAISING_AND_ALLOCATING_FUNDS):
      return (
        <RaisingAndAllocatingFundsPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section8}
          availability={sectionsAvailability.raisingAndAllocatingFunds}
          sectionNumber={getSectionNumber(filledSections, 'raisingAndAllocatingFunds')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.RAISING_AND_ALLOCATING_FUNDS):
      return (
        <RaisingAndAllocatingFundsPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section8}
          availability={sectionsAvailability.raisingAndAllocatingFunds}
          businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
          sectionNumber={getSectionNumber(filledSections, 'raisingAndAllocatingFunds')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case PDF_SECTIONS.LEGAL_FRAMEWORK_AND_RISKS:
      return (
        <LegalFrameworkAndRisksPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section9}
          availability={sectionsAvailability.legalFrameworkAndRisks}
          sectionNumber={getSectionNumber(filledSections, 'legalFrameworkAndRisks')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case PDF_SECTIONS.IMPLEMENTATION_PLAN:
      return (
        <ImplementationPlanPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section10}
          availability={sectionsAvailability.implementationPlan}
          sectionNumber={getSectionNumber(filledSections, 'implementationPlan')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_BANK, PDF_SECTIONS.FINANCIAL_PLAN):
      return (
        <FinancialPlanPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section11}
          availability={sectionsAvailability.financialPlan}
          sectionNumber={getSectionNumber(filledSections, 'financialPlan')}
          isPreviewMode
          openingBalanceSheetData={openingBalanceSheetData}
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    case prepareSectionId(BUSINESS_PLAN_TYPES.FOR_INVESTORS, PDF_SECTIONS.FINANCIAL_PLAN):
      return (
        <FinancialPlanPdf
          {...sections[section]}
          sectionPicture={sectionPictures?.Section11}
          availability={sectionsAvailability.financialPlan}
          businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
          sectionNumber={getSectionNumber(filledSections, 'financialPlan')}
          isPreviewMode
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      );
    default:
      return null;
  }
};

export const getMagazineText = (finalData: ITextItem[][]) => {
  let index = -1;

  return () => {
    index++;
    return finalData[index];
  };
};

export const getSubSectionHeading = (sectionNumber: string) => {
  let index = 0;

  return (heading: string) => {
    index++;
    return `${+sectionNumber}.${index}. ${heading}`;
  };
};

const isInfoTableNotEmpty = (equipmentGroups?: IInfoTableRow[][]) =>
  !!equipmentGroups
    ?.map((row) =>
      row.filter(({ info }) => (Array.isArray(info) ? info.length : typeof info === 'object' ? info?.imgSrc : info)),
    )
    .filter((array) => array.length > 1).length;

const isTableRowsNotEmpty = (rows?: IFinBlockTableRowItem[], excludeFirstArrayItem = false) =>
  Boolean(rows && !checkIsEmptyDataByKey(rows, 'values', excludeFirstArrayItem));

export const getSummarySectionAvailability = (summaryData: ISummaryPdfProps, isInvestor: boolean) => {
  if (!summaryData) {
    return {
      goalsAndObjectives: false,
      companyDescription: false,
      productsOrServices: false,
      marketAndCustomers: false,
      organisationAndTeam: false,
      financialInformation: false,
      financingNeeds: false,
      riskManagementStrategies: false,
    };
  }
  const { magazineItems = [], data: { financialInfo, financingNeedsInfo } = {} } =
    summaryData ?? ({} as ISummaryPdfProps);

  return {
    goalsAndObjectives: isMagazineItemsNotEmpty(magazineItems[0]),
    companyDescription: isMagazineItemsNotEmpty(magazineItems[1]),
    productsOrServices: isMagazineItemsNotEmpty(magazineItems[2]),
    marketAndCustomers: isMagazineItemsNotEmpty(magazineItems[3]),
    organisationAndTeam: isMagazineItemsNotEmpty(magazineItems[4]),
    financialInformation: !!financialInfo?.length,
    financingNeeds: !![financingNeedsInfo]
      .map((row) =>
        row?.filter((item) =>
          Array.isArray(item.info) ? item.info.length : typeof item.info === 'object' ? item.info?.imgSrc : item.info,
        ),
      )
      .filter((array) => array?.length).length,
    riskManagementStrategies: !isInvestor && isMagazineItemsNotEmpty(magazineItems[5]),
  };
};

export const getAboutCompanySectionAvailability = (
  aboutCompanyData: IAboutCompanyPdfProps = {} as IAboutCompanyPdfProps,
  isInvestor: boolean,
) => {
  if (!aboutCompanyData) {
    return {
      generalInformation: false,
      companyHistory: false,
      aboutTheProject: false,
      missionAndStrategy: false,
      stages: false,
      swotAnalysis: false,
    };
  }
  const {
    magazineItems = [],
    generalInfoTableData = [],
    goalsAndObjectivesTableData = {},
    missionAndVisionTableData = [],
    historicalEventsTimelineData = [],
    stages = [],
    swotTableData = {},
  } = aboutCompanyData || {};

  const hasGoalsAndObjectivesData = !getIsGoalsAndObjectivesTableEmpty(goalsAndObjectivesTableData as IGoalsTableData);

  return {
    generalInformation: isMagazineItemsNotEmpty(magazineItems[0]) || generalInfoTableData.some((item) => !!item.info),
    companyHistory:
      (!isInvestor && isMagazineItemsNotEmpty(magazineItems[1])) || hasDataToRender(historicalEventsTimelineData),
    aboutTheProject:
      isInvestor &&
      (isMagazineItemsNotEmpty(magazineItems[1]) ||
        hasGoalsAndObjectivesData ||
        isMagazineItemsNotEmpty(magazineItems[2])),
    missionAndStrategy:
      !isInvestor &&
      (!!missionAndVisionTableData.length || hasGoalsAndObjectivesData || isMagazineItemsNotEmpty(magazineItems[2])),
    stages: isInvestor && !!stages.length,
    swotAnalysis: Object.keys(swotTableData ?? {}).some(
      (key) => swotTableData[key]?.length > 0 && swotTableData[key]?.some((item: { title: string }) => !!item.title),
    ),
  };
};

export const getProductsAndServicesSectionAvailability = (
  productAndServicesData: IProductsAndServicesPdfProps,
  isAvailable = true,
) => {
  if (!isAvailable || !productAndServicesData) {
    return {
      description: false,
      advantages: false,
      rAndD: false,
      deliveryAndSupport: false,
    };
  }

  const {
    magazineItems = [],
    data: { certifications, characteristics, patentsOrTrademarks, developmentTimeline } = {},
  } = productAndServicesData ?? ({} as IProductsAndServicesPdfProps);

  return {
    description:
      isMagazineItemsNotEmpty(magazineItems[0]) ||
      isMagazineItemsNotEmpty(magazineItems[1]) ||
      !!certifications?.length ||
      !!characteristics?.length ||
      !!patentsOrTrademarks?.length,
    advantages: isMagazineItemsNotEmpty(magazineItems[2]),
    rAndD:
      isMagazineItemsNotEmpty(magazineItems[3]) ||
      !!developmentTimeline?.length ||
      isMagazineItemsNotEmpty(magazineItems[4]),
    deliveryAndSupport: isMagazineItemsNotEmpty(magazineItems[5]),
  };
};

export const getMarketAnalysisSectionAvailability = (
  marketAnalysisData: IMarketAnalysisPdfProps,
  isInvestor: boolean,
  isAvailable = true,
) => {
  if (!isAvailable || !marketAnalysisData) {
    return {
      marketOverview: false,
      competitiveAnalysis: false,
      consumerNeeds: false,
    };
  }

  const {
    magazineItems = [],
    data: {
      consumptionForecastImages,
      marketTrends,
      structureDetailsImages,
      priceOverviewChartImage,
      competitorsComparison,
      strengthAndWeakness,
      productComparisonArray,
      B2C,
      B2B,
    } = {},
  } = marketAnalysisData ?? ({} as IMarketAnalysisPdfProps);

  const competitorsFilteredRows = competitorsComparison?.filter(
    ({ info }) => info.filter((item) => (typeof item === 'object' ? item?.value : item)).length,
  );

  const annualSalesVolumeItem = competitorsFilteredRows?.find((row) => row.id === 'annualSalesVolume');

  return {
    marketOverview:
      isMagazineItemsNotEmpty(magazineItems[0]) ||
      (!!consumptionForecastImages?.length &&
        consumptionForecastImages.some((image) => !!image.src && image.src !== emptyChart)) ||
      (!isInvestor && isMagazineItemsNotEmpty(magazineItems[1])) ||
      !!marketTrends?.filter((trend) => trend.trendName || trend.trendCharacteristics).length ||
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 2 : 1]) ||
      (!!structureDetailsImages?.length &&
        structureDetailsImages.some((image) => !!image.src && image.src !== emptyChart)) ||
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 3 : 2]) ||
      (!!priceOverviewChartImage?.src && priceOverviewChartImage.src !== emptyChart),
    competitiveAnalysis:
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 4 : 3]) ||
      ((competitorsFilteredRows?.length ?? 0) > 1 &&
        annualSalesVolumeItem?.info[0] !== '$0.0' &&
        annualSalesVolumeItem?.info[0] !== '$0' &&
        annualSalesVolumeItem?.info[0] !== '$') ||
      !(
        strengthAndWeakness?.headers.filter((header) => header.name || header.image).length === 0 ||
        strengthAndWeakness?.rows.filter(({ items }) => items.filter((item) => item.length > 0).length > 0).length === 0
      ) ||
      (!isInvestor && isMagazineItemsNotEmpty(magazineItems[5])) ||
      productComparisonArray?.some(
        (rows) =>
          !!rows.filter((row) => row.items.filter((item) => (typeof item === 'object' ? item.imgSrc : item)).length)
            .length,
      ) ||
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 6 : 4]),
    consumerNeeds:
      !![B2C]
        .map((row) =>
          row?.filter(({ info }) =>
            Array.isArray(info) ? info.length : typeof info === 'object' ? info?.imgSrc : info,
          ),
        )
        .filter((array) => (array?.length ?? 0) > 1).length ||
      !![B2B]
        .map((row) =>
          row?.filter(({ info }) =>
            Array.isArray(info) ? info.length : typeof info === 'object' ? info?.imgSrc : info,
          ),
        )
        .filter((array) => array?.length).length ||
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 7 : 5]),
  };
};

export const getMarketingAndSalesSectionAvailability = (
  marketingAndSalesData: IMarketingAndSalesPdfProps | undefined | null,
  isInvestor: boolean,
  isAvailable = true,
) => {
  if (!isAvailable || !marketingAndSalesData) {
    return {
      salesPlan: false,
      marketingPlan: false,
    };
  }

  const {
    magazineItems = [],
    data: {
      salesFunnelData,
      salesPlanTableData,
      firstMonetaryChartImageSrc,
      secondMonetaryChartImageSrc,
      physicalValuesChartImageSrc,
      monetaryRatioChartImageSrc,
      monetaryTotalValuesChartImageSrc,
      salesChannelsData,
    } = {},
  } = marketingAndSalesData ?? ({} as IMarketingAndSalesPdfProps);

  return {
    salesPlan:
      isMagazineItemsNotEmpty(magazineItems[0]) ||
      (!isInvestor && (hasDataToRender(salesFunnelData) || isMagazineItemsNotEmpty(magazineItems[1]))) ||
      salesPlanTableData?.rows.some((row) =>
        row.characteristics?.some((characteristic) =>
          characteristic.values?.some((value) => value !== 0 && !isNaN(value)),
        ),
      ) ||
      (!!firstMonetaryChartImageSrc && firstMonetaryChartImageSrc !== emptyChart) ||
      (!!secondMonetaryChartImageSrc && secondMonetaryChartImageSrc !== emptyChart) ||
      (!!physicalValuesChartImageSrc && physicalValuesChartImageSrc !== emptyChart) ||
      (!!monetaryRatioChartImageSrc && monetaryRatioChartImageSrc !== emptyChart) ||
      (!!monetaryTotalValuesChartImageSrc && monetaryTotalValuesChartImageSrc !== emptyChart) ||
      !!salesChannelsData?.length ||
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 2 : 1]),
    marketingPlan: isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 3 : 2]),
  };
};

export const getBusinessOperationsSectionAvailability = (
  businessOperationsData: IBusinessOperationsPdfProps,
  isInvestor: boolean,
  isAvailable = true,
) => {
  if (!isAvailable || !businessOperationsData) {
    return {
      location: false,
      equipment: false,
      assets: false,
      technologicalProcess: false,
      workingResources: false,
      partners: false,
      production: false,
      quality: false,
      productDescription: false,
      advantages: false,
    };
  }

  const {
    magazineItems = [],
    data: {
      buildingsPremisesDetails,
      equipmentGroups,
      plannedPurchases,
      assetGroups,
      futureAcquisitions,
      processStagesImg,
      productionPlanImages,
      supplierOverviewData,
      characteristics,
      certifications,
      patentsOrTrademarks,
    } = {},
  } = businessOperationsData ?? ({} as IBusinessOperationsPdfProps);

  return {
    location:
      isMagazineItemsNotEmpty(magazineItems[0]) ||
      (!isInvestor &&
        (!!buildingsPremisesDetails?.filter(
          ({ info }) => info.filter((item) => (typeof item === 'object' ? item?.value : item)).length,
        ).length ||
          isMagazineItemsNotEmpty(magazineItems[1]))),
    equipment:
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 2 : 1]) ||
      isInfoTableNotEmpty(equipmentGroups) ||
      (!isInvestor && isMagazineItemsNotEmpty(magazineItems[3])) ||
      isInfoTableNotEmpty(plannedPurchases) ||
      (!isInvestor && isMagazineItemsNotEmpty(magazineItems[4])),
    assets:
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 5 : 2]) ||
      isInfoTableNotEmpty(assetGroups) ||
      isInfoTableNotEmpty(futureAcquisitions) ||
      (!isInvestor && isMagazineItemsNotEmpty(magazineItems[6])),
    technologicalProcess:
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 7 : 3]) ||
      !!processStagesImg ||
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 8 : 4]) ||
      (isInvestor && isInfoTableNotEmpty(supplierOverviewData)),
    workingResources: !isInvestor && isMagazineItemsNotEmpty(magazineItems[9]),
    partners: !isInvestor && (isInfoTableNotEmpty(supplierOverviewData) || isMagazineItemsNotEmpty(magazineItems[10])),
    quality: !isInvestor && isMagazineItemsNotEmpty(magazineItems[12]),
    production:
      (!!productionPlanImages?.length && productionPlanImages?.some(({ src }) => !!src && src !== emptyChart)) ||
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 11 : 5]),
    productDescription:
      isInvestor &&
      (isMagazineItemsNotEmpty(magazineItems[6]) ||
        isMagazineItemsNotEmpty(magazineItems[7]) ||
        !!certifications?.length ||
        !!characteristics?.length ||
        !!patentsOrTrademarks?.length),
    advantages: isInvestor && isMagazineItemsNotEmpty(magazineItems[8]),
  };
};

export const getManagementAndOrganizationSectionAvailability = (
  managementAndOrganizationData: IManagementAndOrganizationPdfProps,
  isInvestor: boolean,
  isAvailable = true,
) => {
  if (!isAvailable || !managementAndOrganizationData) {
    return {
      management: false,
      partners: false,
      staff: false,
    };
  }

  const { magazineItems = [], data: { keyPersonnelProfiles, profiles, organizationalStructureImg } = {} } =
    managementAndOrganizationData ?? ({} as IManagementAndOrganizationPdfProps);

  return {
    management: isMagazineItemsNotEmpty(magazineItems[0]) || !!keyPersonnelProfiles?.length,
    partners: !isInvestor && (!!profiles?.length || isMagazineItemsNotEmpty(magazineItems[1])),
    staff:
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 2 : 1]) ||
      !!organizationalStructureImg ||
      isMagazineItemsNotEmpty(magazineItems[!isInvestor ? 3 : 2]),
  };
};

export const getImplementationPlanSectionAvailability = (
  implementationPlanData: IImplementationPlanPdfProps,
  isAvailable = true,
) => {
  if (!isAvailable || !implementationPlanData) {
    return {
      wholePage: false,
    };
  }

  const { data: { stages: implementationPlanStages } = {} } =
    implementationPlanData ?? ({} as IImplementationPlanPdfProps);

  return {
    wholePage: !!implementationPlanStages?.length,
  };
};

export const getRaisingAndAllocatingFundsSectionAvailability = (
  raisingAndAllocatingFundsData: IRaisingAndAllocatingFundsPdfProps,
  isAvailable = true,
) => {
  if (!isAvailable || !raisingAndAllocatingFundsData) {
    return {
      wholePage: false,
    };
  }

  const { magazineItems = [], data: { fundingStrategyImage, projectCostAndFundingNeedsSteps } = {} } =
    raisingAndAllocatingFundsData ?? ({} as IRaisingAndAllocatingFundsPdfProps);

  return {
    wholePage:
      isMagazineItemsNotEmpty(magazineItems[0]) ||
      fundingStrategyImage?.some((item) => !!item.src && item.src !== emptyChart) ||
      isMagazineItemsNotEmpty(magazineItems[1]) ||
      !!projectCostAndFundingNeedsSteps?.length ||
      isMagazineItemsNotEmpty(magazineItems[2]),
  };
};

export const getLegalFrameworkAndRisksSectionAvailability = (
  legalFrameworkAndRisksData: ILegalFrameworkAndRisksPdfProps,
  isAvailable = true,
) => {
  if (!isAvailable || !legalFrameworkAndRisksData) {
    return {
      generalLaws: false,
      industryRequirements: false,
      legalRequirements: false,
      listOfActions: false,
      listOfRisks: false,
    };
  }

  const { magazineItems: legalFrameworkAndRisksMagazineItems = [], data: { listOfActions, listOfRisks } = {} } =
    legalFrameworkAndRisksData ?? ({} as ILegalFrameworkAndRisksPdfProps);

  return {
    generalLaws: isMagazineItemsNotEmpty(legalFrameworkAndRisksMagazineItems[0]),
    industryRequirements: isMagazineItemsNotEmpty(legalFrameworkAndRisksMagazineItems[1]),
    legalRequirements: isMagazineItemsNotEmpty(legalFrameworkAndRisksMagazineItems[2]),
    listOfActions: !!listOfActions?.length,
    listOfRisks: !Object.values(listOfRisks?.data ?? {}).every(({ content }) =>
      content?.every((subArray) => subArray?.every((item) => !item)),
    ),
  };
};

export const getFinancialPlanSectionAvailability = (
  financialPlanData: IFinancialPlanPdfProps = {} as IFinancialPlanPdfProps, // Ensure default object
  isInvestor: boolean,
  isAvailable = true,
) => {
  if (!isAvailable || !financialPlanData) {
    return {
      keyAssumption: false,
      profitLossProjection: false,
      projectedCashFlow: false,
      projectedBalanceSheet: false,
      financialPerformance: false,
    };
  }

  const { magazineItems: financialPlanMagazineItems = [], data = {} as IFinancialPlanPdfProps['data'] } =
    financialPlanData || {};

  const {
    profitAndLossTableData,
    profitAndLossChart,
    plannedExpensesTable,
    plannedExpensesChartImage,
    costPerUnitChartImage,
    variableExpensesTable,
    variableExpensesChartImage,
    donutChartsImages,
    sourcesOfProjectFinancingAndloanRepaymentChartImages,
    profitabilityRatiosChartImage,
    lineChartsImages,
    solvencyRatiosChartImage,
    personnelExpenses,
    projectedBalanceSheetAssetsChart,
    projectedBalanceSheetEquityAndLiabilitiesChart,
    projectedBalanceSheet,
    fundingDirectionsTableData,
    fundingDirectionsChartImage,
    investmentPerformanceRatios,
    otherPerformanceRatios,
    projectedCashFlowTable,
    projectedCashFlowChartImage,
  } = data ?? {}; // Ensure data is always an object

  const hasProjectedBalanceSheetTable = isTableRowsNotEmpty(projectedBalanceSheet?.rows);
  const hasProjectedBalanceSheetAssetsChart =
    !!projectedBalanceSheetAssetsChart?.src && projectedBalanceSheetAssetsChart.src !== emptyChart;
  const hasProjectedBalanceSheetEquityChart =
    !!projectedBalanceSheetEquityAndLiabilitiesChart?.src &&
    projectedBalanceSheetEquityAndLiabilitiesChart.src !== emptyChart;
  const hasProjectedBalanceSheetText = isMagazineItemsNotEmpty(financialPlanMagazineItems[3]);
  const hasProjectedBalanceSheet =
    !isInvestor &&
    (hasProjectedBalanceSheetTable ||
      hasProjectedBalanceSheetAssetsChart ||
      hasProjectedBalanceSheetEquityChart ||
      hasProjectedBalanceSheetText);

  return {
    keyAssumption:
      isMagazineItemsNotEmpty(financialPlanMagazineItems[0]) || isMagazineItemsNotEmpty(financialPlanMagazineItems[1]),
    profitLossProjection:
      isTableRowsNotEmpty(profitAndLossTableData?.rows) ||
      (!!profitAndLossChart?.src && profitAndLossChart.src !== emptyChart) ||
      isTableRowsNotEmpty(plannedExpensesTable?.rows) ||
      (!!plannedExpensesChartImage &&
        !!costPerUnitChartImage &&
        [plannedExpensesChartImage, costPerUnitChartImage]?.some(
          (chart) => !!chart?.src && chart?.src !== emptyChart,
        )) ||
      isTableRowsNotEmpty(variableExpensesTable?.rows) ||
      (!!variableExpensesChartImage?.src && variableExpensesChartImage?.src !== emptyChart) ||
      donutChartsImages?.some(({ src }) => !!src && src !== emptyChart) ||
      isTableRowsNotEmpty(personnelExpenses?.rows),
    projectedCashFlow:
      isTableRowsNotEmpty(projectedCashFlowTable?.rows) ||
      (!!projectedCashFlowChartImage?.src && projectedCashFlowChartImage?.src !== emptyChart) ||
      isMagazineItemsNotEmpty(financialPlanMagazineItems[2]) ||
      sourcesOfProjectFinancingAndloanRepaymentChartImages?.some(({ src }) => !!src && src !== emptyChart) ||
      isTableRowsNotEmpty(fundingDirectionsTableData?.rows) ||
      (!!fundingDirectionsChartImage?.src && fundingDirectionsChartImage?.src !== emptyChart),
    projectedBalanceSheet:
      hasProjectedBalanceSheet,
    financialPerformance:
      isTableRowsNotEmpty(investmentPerformanceRatios?.rows, true) ||
      isMagazineItemsNotEmpty(financialPlanMagazineItems[!isInvestor ? 4 : 3]) ||
      isTableRowsNotEmpty(otherPerformanceRatios?.rows, true) ||
      (!isInvestor &&
        [profitabilityRatiosChartImage, ...(lineChartsImages ?? []), solvencyRatiosChartImage].some(
          (chart) => !!chart?.src && chart?.src !== emptyChart,
        )) ||
      isMagazineItemsNotEmpty(financialPlanMagazineItems[!isInvestor ? 5 : 4]),
  };
};

export const getSectionsAvailability = (
  sections: SectionsType,
  businessPlanType: IBusinessToolType,
  privateSections: { [key in PrivateSectionsEnum]?: boolean },
): SectionsAvailability => {
  const isInvestor = businessPlanType === 'LIGHT';

  return {
    summary: getSummarySectionAvailability(
      sections[isInvestor ? 'summaryInvestor' : 'summary'] as ISummaryPdfProps,
      isInvestor,
    ),
    aboutCompany: getAboutCompanySectionAvailability(
      sections[isInvestor ? 'aboutCompanyInvestor' : 'aboutCompany'] as IAboutCompanyPdfProps,
      isInvestor,
    ),
    productsAndServices: getProductsAndServicesSectionAvailability(
      sections.productAndServices as IProductsAndServicesPdfProps,
      privateSections.productAndServices,
    ),
    marketAnalysis: getMarketAnalysisSectionAvailability(
      sections[isInvestor ? 'marketAnalysisInvestor' : 'marketAnalysis'] as IMarketAnalysisPdfProps,
      isInvestor,
      privateSections.marketAnalysis,
    ),
    marketingAndSales: getMarketingAndSalesSectionAvailability(
      (sections[isInvestor ? 'marketingAndSalesInvestor' : 'marketingAndSales'] as IMarketingAndSalesPdfProps) ?? {},
      isInvestor,
      privateSections.marketingAndSales,
    ),
    businessOperations: getBusinessOperationsSectionAvailability(
      sections[isInvestor ? 'businessOperationsInvestor' : 'businessOperations'] as IBusinessOperationsPdfProps,
      isInvestor,
      privateSections.businessOperations,
    ),
    managementAndOrganization: getManagementAndOrganizationSectionAvailability(
      sections[
        isInvestor ? 'managementAndOrganizationInvestor' : 'managementAndOrganization'
      ] as IManagementAndOrganizationPdfProps,
      isInvestor,
      privateSections.managementAndOrganization,
    ),
    implementationPlan: getImplementationPlanSectionAvailability(
      sections['implementationPlan'] as IImplementationPlanPdfProps,
      privateSections.implementationPlan,
    ),
    raisingAndAllocatingFunds: getRaisingAndAllocatingFundsSectionAvailability(
      sections[
        isInvestor ? 'raisingAndAllocatingFundsInvestor' : 'raisingAndAllocatingFunds'
      ] as IRaisingAndAllocatingFundsPdfProps,
      privateSections.raisingAndAllocatingFunds,
    ),
    legalFrameworkAndRisks: getLegalFrameworkAndRisksSectionAvailability(
      sections['legalFrameworkAndRisks'] as ILegalFrameworkAndRisksPdfProps,
      privateSections.legalFrameworkAndRisks,
    ),
    financialPlan: getFinancialPlanSectionAvailability(
      sections[isInvestor ? 'financialPlanInvestor' : 'financialPlan'] as IFinancialPlanPdfProps,
      isInvestor,
      privateSections.businessOperations,
    ),
  };
};
export const getPreviewSectionsAvailability = (
  businessPlanChapters: IBusinessToolForm,
  businessPlanType: IBusinessToolType,
  privateSections: { [key in PrivateSectionsEnum]?: boolean },
  currentAssetsVariants: string[],
  nonCurrentAssetsVariants: string[],
  currentLiabilitiesVariants: string[],
  nonCurrentLiabilitiesVariants: string[],
  equityVariants: string[],
  operatingExpensesLinkedFields: string[],
  adjustmentsForLinkedFieldsValue: typeof adjustmentsForLinkedFields,
  operatingAssetsAndLiabilitiesLinkedFieldsValue: typeof operatingAssetsAndLiabilitiesLinkedFields,
  operatingActivitiesRowsValues: typeof operatingActivitiesRows,
  isExamplePage: boolean,
): SectionsAvailability => {
  const isInvestor = businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS;

  const magazineItemsSummary = getMagazineItemsSummary(businessPlanChapters, isInvestor, isExamplePage);
  const financialInfoSummary = getFinancialInformation(businessPlanChapters, businessPlanType, isExamplePage);
  const financingNeedsInfoSummary = getFinancingNeeds(businessPlanChapters, isInvestor, isExamplePage);

  const {
    magazineItems: magazineItemsAboutCompany,
    generalInfoTableData,
    missionAndVisionTableData,
    goalsAndObjectivesTableData,
    swotTableData,
    historicalEventsTimelineData,
  } = getAboutCompanyProps(businessPlanChapters, businessPlanType, isExamplePage);

  const stages = getStages(businessPlanChapters);

  const { data: dataProductsAndServices, magazineItems: magazineItemsProductsAndServices } = getProductAndServicesProps(
    businessPlanChapters,
    isExamplePage,
  );

  const magazineItemsMarketAnalysis = getMagazineItemsMarketAnalysis(
    businessPlanChapters,
    businessPlanType,
    isExamplePage,
  );
  const marketTrends = getMarketTrends(businessPlanChapters);
  const priceOverviewChart = getPriceOverview(businessPlanChapters, isExamplePage);
  const competitorsComparison = getCompetitorsComparison(businessPlanChapters, isExamplePage);
  const strengthAndWeakness = getStrengthAndWeakness(businessPlanChapters, isExamplePage);
  const productComparisonArray = getProductComparison(businessPlanChapters, isExamplePage);
  const B2C = getB2C(businessPlanChapters, isExamplePage);
  const B2B = getB2B(businessPlanChapters, isExamplePage);

  const { magazineTexts, salesChannelsData, salesFunnelData } = getMarketingAndSalesProps(
    businessPlanChapters,
    businessPlanType,
    isExamplePage,
  );
  const salesPlanTableData = getSalesPlanTableData(businessPlanChapters, isExamplePage);

  const magazineItemsBusinessOperations = getMagazineItemsBusinessOperations(
    businessPlanChapters,
    businessPlanType,
    isExamplePage,
  );

  const buildingsPremisesDetails = getBuildingsPremisesDetails(businessPlanChapters, isExamplePage);
  const { equipmentGroups, plannedPurchases, assetGroups, futureAcquisitions } = getEquipments(
    businessPlanChapters,
    isExamplePage,
  );
  const supplierOverviewData = getSupplierOverviewData(businessPlanChapters, isExamplePage);
  const { processStages } = getProcessStages(businessPlanChapters);
  const productAndServicesData = getProductDescriptionData(businessPlanChapters);

  const magazineItemsManagementAndOrganization = getMagazineItemsManagementAndOrganization(
    businessPlanChapters,
    businessPlanType,
    isExamplePage,
  );
  const keyPersonnelProfiles = getKeyPersonnelProfiles(businessPlanChapters, isExamplePage);
  const profiles = getProfiles(businessPlanChapters, isExamplePage);

  const magazineItemsRaisingAndAllocatingFunds = getMagazineItemsRaisingAndAllocatingFunds(
    businessPlanChapters,
    businessPlanType,
    isExamplePage,
  );
  const fundingStrategy = getFundingStrategy(businessPlanChapters, isExamplePage);
  const { steps: projectCostAndFundingNeedsSteps } = getProjectCostAndFundingNeeds(
    businessPlanChapters,
    isInvestor,
    isExamplePage,
  );

  const magazineItemsLegalFrameworkAndRisks = getMagazineItemsLegalFrameworkAndRisks(businessPlanChapters);
  const listOfActions = getListOfActions(businessPlanChapters);
  const listOfRisks = getListOfRisks(businessPlanChapters, isExamplePage);

  const dataMarketAnalysis = {
    marketTrends,
    priceOverviewChartImage: {
      title: priceOverviewChart.title,
      subTitle: priceOverviewChart.subtitle,
    },
    competitorsComparison,
    strengthAndWeakness,
    productComparisonArray,
    B2C,
    B2B,
  };

  const dataMarketingAndSales = {
    salesFunnelData,
    salesChannelsData,
    salesPlanTableData,
  };

  const dataBusinessOperations = {
    buildingsPremisesDetails,
    equipmentGroups,
    plannedPurchases,
    assetGroups,
    futureAcquisitions,
    supplierOverviewData,
    processStagesNumber: processStages.length,
    ...productAndServicesData,
  };

  const dataManagementAndOrganization = {
    keyPersonnelProfiles,
    profiles,
  };

  const dataImplementationPlan = {
    stages,
  };

  const dataRaisingAndAllocatingFunds = {
    fundingStrategy,
    projectCostAndFundingNeedsSteps,
  };

  const dataLegalFrameworkAndRisks = {
    listOfActions,
    listOfRisks,
  };
  const summarySections = {
    magazineItems: magazineItemsSummary,
    data: {
      financialInfo: financialInfoSummary,
      financingNeedsInfo: financingNeedsInfoSummary,
    },
  } as ISummaryPdfProps;

  const aboutCompanySections = {
    magazineItems: magazineItemsAboutCompany,
    generalInfoTableData,
    missionAndVisionTableData,
    goalsAndObjectivesTableData,
    swotTableData,
    historicalEventsTimelineData,
    stages,
    historicalEventsTimelineImageSrc: '',
  } as unknown as IAboutCompanyPdfProps;

  const productsAndServicesSections = {
    magazineItems: magazineItemsProductsAndServices,
    data: dataProductsAndServices,
  } as IProductsAndServicesPdfProps;

  const marketAnalysisSections = {
    magazineItems: magazineItemsMarketAnalysis,
    data: dataMarketAnalysis,
  } as unknown as IMarketAnalysisPdfProps;

  const marketingAndSalesSections = {
    magazineItems: magazineTexts,
    data: dataMarketingAndSales,
  } as IMarketingAndSalesPdfProps;

  const businessOperationsSections = {
    magazineItems: magazineItemsBusinessOperations,
    data: dataBusinessOperations,
  } as IBusinessOperationsPdfProps;

  const managementAndOrganizationSections = {
    magazineItems: magazineItemsManagementAndOrganization,
    data: dataManagementAndOrganization,
  } as IManagementAndOrganizationPdfProps;

  const implementationPlanSections = { data: dataImplementationPlan } as IImplementationPlanPdfProps;

  const raisingAndAllocatingFundsSections = {
    magazineItems: magazineItemsRaisingAndAllocatingFunds,
    data: dataRaisingAndAllocatingFunds,
  } as IRaisingAndAllocatingFundsPdfProps;

  const legalFrameworkAndRisksSections = {
    magazineItems: magazineItemsLegalFrameworkAndRisks,
    data: dataLegalFrameworkAndRisks,
  } as ILegalFrameworkAndRisksPdfProps;

  const magazineItemsFinancialPlan = getMagazineItemsFinancialPlan(businessPlanChapters, businessPlanType, isExamplePage);
  const { plannedExpensesTable } = getPlannedExpenses(businessPlanChapters, isExamplePage);
  const { variableExpensesTable } = getVariableExpenses(businessPlanChapters, isExamplePage);
  const personnelExpenses = getPersonnelExpensesData(businessPlanChapters, isExamplePage);
  const projectedBalanceSheet = getProjectedBalanceSheet(
    businessPlanChapters,
    currentAssetsVariants,
    nonCurrentAssetsVariants,
    currentLiabilitiesVariants,
    nonCurrentLiabilitiesVariants,
    equityVariants,
    isExamplePage
  );
  const fundingDirectionsTableData = getFundingDirectionsTableData(businessPlanChapters, isExamplePage);
  const investmentPerformanceRatios = getInvestmentPerformanceRatios(businessPlanChapters, isExamplePage);
  const otherPerformanceRatios = getOtherPerformanceRatios(businessPlanChapters, businessPlanType, isExamplePage);
  const projectedCashFlowTable = getProjectedCashFlowTableData(
    businessPlanChapters,
    adjustmentsForLinkedFieldsValue,
    operatingAssetsAndLiabilitiesLinkedFieldsValue,
    operatingActivitiesRowsValues,
    isExamplePage
  );
  const profitAndLossTableData = getProfitAndLossProjectionTableData(
    businessPlanChapters,
    operatingExpensesLinkedFields,
    isExamplePage
  );

  const dataFinancialPlan = {
    plannedExpensesTable,
    variableExpensesTable,
    personnelExpenses,
    projectedBalanceSheet,
    fundingDirectionsTableData,
    investmentPerformanceRatios,
    otherPerformanceRatios,
    projectedCashFlowTable,
    profitAndLossTableData,
  };

  const financialPlanSections = {
    magazineItems: magazineItemsFinancialPlan,
    data: dataFinancialPlan,
  } as IFinancialPlanPdfProps;

  return {
    summary: getSummarySectionAvailability(summarySections, isInvestor),
    aboutCompany: getAboutCompanySectionAvailability(aboutCompanySections, isInvestor),
    productsAndServices: getProductsAndServicesSectionAvailability(
      productsAndServicesSections,
      privateSections.productAndServices,
    ),
    marketAnalysis: getMarketAnalysisSectionAvailability(
      marketAnalysisSections,
      isInvestor,
      privateSections.marketAnalysis,
    ),
    marketingAndSales: getMarketingAndSalesSectionAvailability(
      marketingAndSalesSections,
      isInvestor,
      privateSections.marketingAndSales,
    ),
    businessOperations: getBusinessOperationsSectionAvailability(
      businessOperationsSections,
      isInvestor,
      privateSections.businessOperations,
    ),
    managementAndOrganization: getManagementAndOrganizationSectionAvailability(
      managementAndOrganizationSections,
      isInvestor,
      privateSections.managementAndOrganization,
    ),
    implementationPlan: getImplementationPlanSectionAvailability(
      implementationPlanSections,
      privateSections.implementationPlan,
    ),
    raisingAndAllocatingFunds: getRaisingAndAllocatingFundsSectionAvailability(
      raisingAndAllocatingFundsSections,
      privateSections.raisingAndAllocatingFunds,
    ),
    legalFrameworkAndRisks: getLegalFrameworkAndRisksSectionAvailability(
      legalFrameworkAndRisksSections,
      privateSections.legalFrameworkAndRisks,
    ),
    financialPlan: getFinancialPlanSectionAvailability(
      financialPlanSections,
      isInvestor,
      privateSections.businessOperations,
    ),
  };
};
